import { createContext, ReactNode, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { Post } from "src/api/client-api/post/postSchema";
import { useGetProductPost } from "src/api/client-api/post/useGetProductPost";
import {
    ProductReferenceScores,
    useGetProductReferenceScores,
} from "src/api/client-api/productReferenceScores/useGetProductReferenceScores";
import { Product, useGetOneProduct } from "src/api/client-api/products/useGetOneProduct";
import { Ssq, useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { ScoreService, ScoreVariation } from "src/context/EntityBasedScore/ScoreService";
import { ScoreState, useEntityBasedScoreParams } from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { SelectVariationCallback } from "src/context/EntityBasedScore/useVariations";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Score } from "src/types";
import { ProductReference } from "src/api/sure-api/useProductReference";
import { ProductSummary, useProductSummaryData } from "./useProductSummaryData";
import { ProductDetailBuilder } from "./ProductDetailBuilder";

type ProductContextInitialValues = {
    product: Product | undefined;
    productReferenceScores: ProductReferenceScores;
    variations: Record<number, number>;
    setVariations: (variations: Record<number, number>) => void;
    isLoading: boolean;
    isRefetching: boolean;

    ssq: Ssq | undefined;
    isSsqLoading: boolean;
    isMonolithic: boolean | undefined;

    post: Post | undefined;
    isPostLoading: boolean;
    allScoreWithVariations: ScoreVariation[];
    scoreState: ScoreState;
    productScores: Score[];
    selectVariation: SelectVariationCallback;
    selectedScore: ScoreVariation;
    productReference?: ProductReference | undefined;
    productSummary: ProductSummary;
    productDetailBuilder: ProductDetailBuilder;
};

const ProductContext = createContext<ProductContextInitialValues | null>(null);

type ProductContextProviderProps = { children: ReactNode };
export const ProductContextProvider = (props: ProductContextProviderProps) => {
    const { children } = props;
    const { productId } = useParams();
    const [variations, setVariations] = useState<Record<number, number>>({});
    const { selectedCompany, currentCampaign, getScoresByType } = useFoodpilotContext();

    // BELOW I SHOULD GO DO THE PRODUCT METADATA
    // ABOVE I SHOULD GO DO THE PRODUCT METADATA

    // BELOW IS THE BLOC FOR PRODUCT SCORE CONTEXT
    const entityParams = useEntityBasedScoreParams({
        campaign: currentCampaign?.id,
        initDimensions: {
            dimensions: [["headings", "subheadings", "properties", "subproperties", "multivalues"]],
        },
        initVariations: {
            variations,
        },
    });

    const { product, isLoading, isRefetching } = useGetOneProduct(productId ?? "A completely bad ID", {
        campaign: currentCampaign?.id,
        company_id: selectedCompany?.id,
        scoreQuery: entityParams.query,
        variations: variations,
        dimensions: entityParams.state.dimensions,
    });
    const ssqId = product?.ssqId;

    const { productReferenceScores = [] } = useGetProductReferenceScores(productId ?? "0");

    const { ssq, isLoading: isSsqLoading } = useGetOneSsq(ssqId);
    const isMonolithic = ssq?.monolithic;

    const { post, isLoading: isPostLoading } = useGetProductPost(
        product?.id,
        selectedCompany?.id ?? null,
        currentCampaign?.id,
    );

    const productScores = getScoresByType("product");

    const allScoreWithVariations = ScoreService.getScoreList(
        productScores,
        product?.scores,
        (scoreId, scoreVariationId) => {
            entityParams.state.selectVariation(scoreId, scoreVariationId);
        },
        entityParams.state.lastSelectedVariation,
    );
    // ABOVE IS THE BLOC FOR PRODUCT SCORE CONTEXT

    const productSummary = useProductSummaryData({
        product,
        ssq,
        post,
        campaignId: currentCampaign?.id,
        selectedScoreId: allScoreWithVariations.lastSelected.scoreId,
    });

    if (post === undefined || ssq === undefined) return;
    const productDetailBuilder = new ProductDetailBuilder({
        productMetadata: {
            productIngredients: product?.productIngredients,
        },
        post,
        ssq,
        scoreId: allScoreWithVariations.lastSelected.scoreId,
        scores: {
            scores: product?.scores,
        },
        scorePrecision: allScoreWithVariations.lastSelected.precision,
        summaryData: productSummary,
    });

    const items: ProductContextInitialValues = {
        product,
        productReferenceScores,
        isLoading,
        isRefetching,
        ssq,
        isSsqLoading,
        isMonolithic,
        post,
        isPostLoading,
        variations,
        setVariations,
        allScoreWithVariations: allScoreWithVariations.scoresWithVariations,
        selectedScore: allScoreWithVariations.lastSelected,
        productScores,
        scoreState: entityParams.state,
        selectVariation: entityParams.state.selectVariation,
        productSummary,
        productDetailBuilder,
    };

    return <ProductContext.Provider value={items}>{children}</ProductContext.Provider>;
};

export const useProductContext = () => {
    const context = useContext(ProductContext);

    if (context) {
        return context;
    }

    throw new Error(`useProductContext must be used within an ProductContextProvider`);
};
